import React, { useContext, useState } from 'react';
import { Evaluation, Scorecard } from '../types/Scorecard';
import { EnrichedRegistrant, RegistrantAnswer } from '../types/Registrant';
import { EventContext } from '../context/EventContext';
import { createEvaluation } from '../services/evaluationService';
import { useToast } from '../context/ToastContext';
import ToggleSwitch from './ToggleSwitch';

interface Props {
  setCurrentScorecard: (scorecard: Scorecard) => void;
  scorecards: Scorecard[];
  registrant: EnrichedRegistrant;
  closePopup: () => void;
  scorecard: Scorecard | null;
  ignoreScore: boolean;
  toggleIgnoreScore: () => void;
}

export const JudgeRegistrantPopup: React.FC<Props> = ({
  setCurrentScorecard,
  scorecards,
  registrant,
  closePopup,
  scorecard,
  ignoreScore,
  toggleIgnoreScore,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [commentText, setCommentText] = useState(''); // State for comment
  const [overWriteScore, setOverWriteScore] = useState<number | null>(null)
  const { currentEvent } = useContext(EventContext);
  const [showErrors, setShowErrors] = useState(false);
  const { addToast } = useToast();

  const totalPossiblePoints = scorecard?.question_data
    ? scorecard.question_data.reduce((sum, question) => sum + question.point_value, 0)
    : 0;
  const calculatedScore = Object.values(answers).reduce((sum, value) => sum + value, 0);


  const handleScoreChange = (index: number, value: string) => {
    const sanitizedValue = value === '' ? null : Math.max(0, Math.min(Number(value), scorecard?.question_data?.[index]?.point_value || 0));
    setAnswers((prev) => ({ ...prev, [index]: sanitizedValue }));
  };


  const handleScorecardSelect = (selectedScorecard: Scorecard) => {
    setCurrentScorecard(selectedScorecard);
    setShowDropdown(false);
  };

  const handleCreateEvaluation = async () => {
    setShowErrors(false);

    // Ensure an event exists
    if (!currentEvent) {
      setShowErrors(true);
      addToast("No Event", true);
      return;
    }

    // Validate answers if not ignoring the score
    if (!ignoreScore) {
      const hasEmptyValues = Object.values(answers).some(value => value === null || value === undefined);
      if (hasEmptyValues || Object.keys(answers).length === 0) {
        addToast("Please fill out all fields or check 'Skip Criteria'.", true);
        setShowErrors(true);
        return;
      }
    } else {
      // Validate overwritten score when ignoring score
      if (overWriteScore === null || overWriteScore === undefined) {
        addToast("Please fill out a score even if 0.", true);
        setShowErrors(true);
        return;
      }
    }

    // Calculate the total score
    const score = ignoreScore ? (overWriteScore ?? 0) : (calculatedScore ?? 0);

    // Safely access question_data with a fallback to an empty array
    const questionData = scorecard?.question_data ?? [];
    const denominator = questionData.reduce((sum, question) => sum + question.point_value, 0) ?? 100;
    // Create the evaluation object

    // Log or handle the new evaluation
    try {
      const newEvaluation: Evaluation = {
        evaluation_id: '',  // Placeholder for later population
        registrant_id: registrant.registrant_id,
        judge_id: '',  // Placeholder for judge ID
        event_id: currentEvent?.event_id,
        total_score: 100.0 * score / denominator,
        results: ignoreScore
          ? []  // No results if ignoring score
          : questionData.map((question, index) => ({
            question: question.question, // Aligning with `Question` type
            point_value: answers[index] || 0,//may need to fix here
          })),
        comments: commentText,
      };
      // Log and create evaluation
      await createEvaluation(newEvaluation);
      addToast("Evaluation created successfully!");
      closePopup(); // Close the popup upon success
    } catch (error) {
      console.error("Failed to create evaluation:", error);
      addToast("An error occurred while creating the evaluation. Please try again.", true);
    }

    // Add the logic to save or send the evaluation as needed
  };

  const handleBlur = (index: number, value: string) => {
    let num = Number(value);

    if (isNaN(num)) {
      setAnswers((prev) => ({ ...prev, [index]: null }));
      return;
    }

    const maxPoint = scorecard?.question_data?.[index]?.point_value || 0;

    // Clamp between 0 and max
    num = Math.max(0, Math.min(num, maxPoint));

    // Truncate (not round) to 2 decimal places
    num = Math.floor(num * 100) / 100;

    setAnswers((prev) => ({ ...prev, [index]: num }));
  };

  const handleOverwriteBlur = (value: string) => {
    let num = Number(value);

    const maxPoint = value;

    // Clamp between 0 and max

    // Truncate (not round) to 2 decimal places
    num = Math.floor(num * 100) / 100;

    setOverWriteScore(num);
  };


  return (
    <div className="popOverContainer" style={{ maxWidth: '95vw', maxHeight: '90vh', minWidth: '40vw' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', rowGap: '20px' }}><h2>
        Evaluate: {registrant.last_name},{' '}
        {registrant.first_name}
      </h2>

      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          justifyContent: 'space-between'
        }}
      >
        {/* <h4 style={{ margin: 0 }}>{scorecard ? `Using:` : `` } {scorecard?.scorecard_name}</h4>
        {/* {scorecards.length > 1 && (
          <div>

            <button
              // style={{
              //   color: 'blue',
              //   textDecoration: 'underline',
              //   cursor: 'pointer',
              //   background: 'none',
              // }}
              onClick={() => setShowDropdown((prev) => !prev)}
            >
              <i>{scorecard ? `Switch Scorecards` : `Select A Scorecard`}</i>
            </button>
            {showDropdown && (
              <ul
                style={{
                  border: '1px solid #ccc',
                  padding: '10px',
                  margin: '10px 0',
                  listStyleType: 'none',
                }}
              >
                {scorecards.map((sc) => (
                  <li
                    key={sc.scorecard_id}
                    style={{
                      padding: '5px',
                      cursor: 'pointer',
                      backgroundColor:
                        sc.scorecard_id === scorecard?.scorecard_id
                          ? '#f0f0f0'
                          : 'transparent',
                    }}
                    onClick={() => handleScorecardSelect(sc)}
                  >
                    {sc.scorecard_name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )} */}

        {scorecards.length > 1 && (
          <div>
            <select
              value={scorecard?.scorecard_id || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedScorecard = scorecards.find(sc => sc.scorecard_id === selectedId);
                if (selectedScorecard) {
                  handleScorecardSelect(selectedScorecard);
                }
              }}
              style={{ padding: '5px', minWidth: '300px', maxWidth: '90vw' }}
            >
              <option value="" disabled>
                Select a Scorecard
              </option>
              {scorecards.map((sc) => (
                <option key={sc.scorecard_id} value={sc.scorecard_id}>
                  {sc.scorecard_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {scorecard && <ToggleSwitch
          label={'Hide Questions'}
          checked={ignoreScore}
          onChange={toggleIgnoreScore}
          hideToggle={false}
        />}
      </div>
      {/* Render questions and inputs */}
      {scorecard?.question_data && !ignoreScore && (

        <div style={{ marginTop: '20px', overflowY: 'auto' }}>
          <h3>Scorecard Questions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {scorecard.question_data.map((question, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  justifyContent: 'space-between',
                }}
              >
                <label style={{ flex: 1 }}>{question.question}</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  maxLength={6}
                  max={question.point_value}
                  onBlur={(e) => handleBlur(index, e.target.value)}
                  value={answers[index] !== null && answers[index] !== undefined ? answers[index] : ''}
                  onChange={(e) => handleScoreChange(index, e.target.value)}
                  style={{
                    width: '60px',
                    textAlign: 'center',
                    borderColor: showErrors && (answers[index] === null || answers[index] === undefined) ? '#DD5555' : '#ccc',
                  }}
                />

                <span> / {question.point_value}</span>
              </div>
            ))}
          </div>
        </div>
      )}


      {!ignoreScore ? (<div style={{ paddingTop: '20px', display: 'flex' }}>
        <label><strong>Total Score</strong></label>
        <label style={{ marginLeft: 'auto' }}><strong>{calculatedScore} / {totalPossiblePoints}</strong></label>
      </div>) :
        <div style={{ paddingTop: '60px', display: 'flex' }}>
          <label><strong>Overwritten Score</strong></label>
          <div style={{ marginLeft: 'auto' }}>
            <input
              type="number"
              min={0}
              step={0.5}
              maxLength={6}
              max={totalPossiblePoints}
              value={overWriteScore !== null && overWriteScore !== undefined ? overWriteScore : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setOverWriteScore(null); // Set to null for empty input
                } else {
                  setOverWriteScore(Math.max(0, Math.min(totalPossiblePoints, Number(value))));
                }
              }}
              onBlur={(e) => handleOverwriteBlur(e.target.value)}
              style={{
                width: '60px',
                textAlign: 'center',
                borderColor: showErrors && (overWriteScore === null || overWriteScore === undefined) ? '#DD5555' : '#ccc', // Replace 'initial' with your default color if needed
              }}
            />

            <label><strong> / {totalPossiblePoints}</strong></label>
          </div>
        </div>
      }

      {/* Comment Box */}
      <textarea
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
        placeholder="Enter your comments here..."
        style={{
          // width: '80%',
          height: '50px',
          marginTop: '20px',
          padding: '10px',
          fontSize: '14px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          resize: 'none',
        }}
      />

      <div className="buttonsRow" style={{ marginTop: '10px' }}>
        {/* Cancel Button */}
        <div className='buttonsLeft'>
          <button
            style={{
              fontSize: '16px',
              backgroundColor: '#888888',
            }}
            onClick={closePopup}
          >
            Cancel
          </button>

        </div>
        <div className='buttonsRight'>
          <button onClick={() => handleCreateEvaluation()}>
            Save
          </button>
        </div>

      </div>
    </div>
  );
};

export default JudgeRegistrantPopup;
